"use client";
import React, { useState, useRef, useEffect, useMemo } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import CalcInputValues from "../ui/CalcInputValues";
import { useIsMobile } from "@/utils/useIsMobile";
import CustomModal from "@/components/modal/CustomModal";

import { formatIndianNumber } from "@/utils/commonFunctions";
import Image from "next/image";
import Icon from "@/components/icons/Icons";
import CustomDropdown from "@/components/ui/CustomDropdown";
import { apiPost } from "@/utils/apiClient";
import { AnimatePresence, motion } from "framer-motion";
import { allAmcFutureCalculation } from "@/utils/allCalculators";
import { useDispatch } from "react-redux";
import { openInvestmentModal } from "@/redux/features/InvestmentModal/investmentModalSlice";
import { AmcFund, FundResponse, ChartReportData, MoreAmcCalculator, FutureCalculationResult } from "@/type/amcPageTypes";
import { ROOT_URL } from "@/utils/gloableVariables";

export default function AmcCalc({ amcCode }: { amcCode?: string }) { 
  const [isOpen, setIsOpen] = useState(false);

  const [allFundsList, setAllFundsList] = useState<AmcFund[]>([]);
  const [selectedCalcType, setSelectedCalcType] = useState<"historical" | "future">("historical");

  const [selectedFund, setSelectedFund] = useState<AmcFund | null>(null);
  const [moreCalc, setMoreCalc] = useState<MoreAmcCalculator[]>([]);
  const [fundsData, setFundsData] = useState<FundResponse | null>(null);
  const [isDropdownOpenFund, setIsDropdownOpenFund] = useState(false);
  const [isDropdownOpenYear, setIsDropdownOpenYear] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [investmentAmount, setInvestmentAmount] = useState(25000);
  const [investmentMode, setInvestmentMode] = useState<string>("SIP");
  const [maxInvestmentAmount, setMaxInvestmentAmount] = useState(250000);
  const [minInvestmentAmount, setMinInvestmentAmount] = useState(2500);
  const [selectedYear, setSelectedYear] = useState<number>(1);

  const [investmentModeFuture, setInvestmentModeFuture] = useState<string>("SIP");
  const [investmentFutureAmount, setInvestmentFutureAmount] = useState(25000);
  const [tenure, setTenure] = useState(5);
  const [annualReturns, setAnnualReturns] = useState(12);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const buttonRef2 = useRef<HTMLButtonElement>(null);

  const [calculationHistoric, setCalculationHistoric] = useState<ChartReportData>({});
  const [calculationFuture, setCalculationFuture] = useState<FutureCalculationResult>();

  const [isLoadingReturns, setIsLoadingReturns] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    const result = allAmcFutureCalculation(investmentModeFuture, investmentFutureAmount, tenure, annualReturns);
    setCalculationFuture(result);
  }, [investmentModeFuture, investmentFutureAmount, tenure, annualReturns]);

  const isTab = useIsMobile(1025);
  const isMob = useIsMobile(769);

  // Filter Funds List
  const filteredFunds = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return allFundsList;

    // Filter + sort: matching funds appear first
    return allFundsList.filter((f) => f.scheme_name.toLowerCase().includes(term)).sort((a, b) => a.scheme_name.toLowerCase().indexOf(term) - b.scheme_name.toLowerCase().indexOf(term));
  }, [allFundsList, searchTerm]);

  const availableYears = [1, 3, 5].filter((year) => {
    if (!fundsData?.fund) return false;

    if (year === 1) return fundsData.fund.one_year_abs != null;
    if (year === 3) return fundsData.fund.three_year != null;
    if (year === 5) return fundsData.fund.five_year != null;

    return false;
  });

  // if no valid year values found, default to [1]
  const displayYears = availableYears.length > 0 ? availableYears : [1];

  //Update changing to sip & lumpsum
  useEffect(() => {
    if (investmentMode === "SIP") {
      setMaxInvestmentAmount(250000);
      setMinInvestmentAmount(2500);
      if (fundsData) setCalculationHistoric(fundsData.yearWiseData);
    } else {
      setMaxInvestmentAmount(2500000);
      setMinInvestmentAmount(25000);
      if (fundsData) setCalculationHistoric(fundsData.yearWiseDataLumpsum.yearWiseData);
    }
  }, [investmentMode, fundsData, investmentModeFuture]);

  // AMC Funds list to select
  useEffect(() => {
    const payload = {
      type: "funds",
      action: "search_header",
      default_filter: {
        amc_code: amcCode,
      },
    };

    async function fetchAllFundsList() {
      try {
        const data = await apiPost(`${ROOT_URL}web-apis.php`, payload, { base: "none" });
        setAllFundsList(data.data);
      } catch (err) {
        console.error(`${err} | Something Went Wrong`);
      }
    }

    fetchAllFundsList();
  }, []);

  // Selected AMC Funds Data
  useEffect(() => {
    if (!selectedFund) return;

    const payload = {
      isin: selectedFund.u_code,
      scheme_code: selectedFund.scheme_code,
      amc_code: amcCode,
      kra: selectedFund.kra,
      amount: investmentAmount,
      range: `${selectedYear} year`,
      action: "sip_calculator",
    };

    async function fetchSelectedFundsData() {
      try {
        const data = await apiPost(`${ROOT_URL}web-apis.php`, payload, { base: "none" });
        setFundsData(data);
      } catch (err) {
        console.error(`${err} | Something Went Wrong`);
      } finally {
        setIsLoadingReturns(false);
      }
    }

    fetchSelectedFundsData();
  }, [selectedFund, investmentAmount, selectedYear]);

  useEffect(() => {
    setSelectedFund(allFundsList[0]);
  }, [allFundsList]);

  useEffect(() => {
    const loadAmcs = async () => {
      setLoading(true);

      try {
        const res = await fetch(`/api/amcCalculators?excludeAlt=${amcCode}`, {
          cache: "no-store",
        });
        const data = await res.json();
        setMoreCalc(data);
        setLoading(false);
      } catch (error) {
        console.error(`Something went wrong - Error: ${error}`);
        setLoading(false);
      }
    };

    loadAmcs();
  }, []);

  const handleSelectFund = (value: AmcFund) => {
    setIsDropdownOpenFund(false);
    setSelectedFund(value);
  };

  const handleSelectYear = (value: number) => {
    setIsDropdownOpenYear(false);
    setSelectedYear(value);
  };

  const fundYearMap: Record<number, number | undefined> = {
    1: fundsData?.fund?.one_year_abs,
    2: fundsData?.fund?.two_year,
    3: fundsData?.fund?.three_year,
    5: fundsData?.fund?.five_year,
  };

  const fundsReturns = fundYearMap[selectedYear] ?? 0;

  const selectedSchemeData = fundsData?.schemeData;

  const schemeData = { scheme_code: selectedSchemeData?.scheme_code, scheme_name: selectedSchemeData?.scheme_name, amc_code: selectedSchemeData?.amc_code, sip_allowed: selectedSchemeData?.sip_allowed, lump_allowed: selectedSchemeData?.lump_allowed, minimum_sip_invest: selectedSchemeData?.minimum_sip_invest, minimum_investment: selectedSchemeData?.minimum_investment };

  return (
    <>
      {/* Hero Heading */}
      <section className="relative text-white text-center mb-12 sm:mb-100 section">
        {/* Floating Calculator */}
        <div className="relative mx-auto w-full xs:max-w-[90%] lg:max-w-302.5 xs:bg-white xs:rounded-xl xs:shadow-lg sm:overflow-hidden">
          <div className="p-4 pb-6 sm:pb-0 xl:p-6 xl:pb-0 text-primaryBlack bg-white rounded-xl shadow-[0px_0px_10px_0_#00000047] xs:bg-transparent xs:rounded-none xs:shadow-none">
            <div className="flex flex-col 2md:flex-row items-center justify-center gap-3 2md:gap-5 mb-4 2lg:mb-8">
              <p className="xs:text-lg sm:text-2xl lg:text-2.5xl font-semibold text-center text-primaryGreen">What you would like to Calculate?</p>

              <div className="flex gap-2.5 sm:gap-4"> 
                {/* Historical Returns */}
                <button onClick={() => setSelectedCalcType("historical")} className={`flex items-center gap-1.5 xs:gap-2 px-2 sm:px-4 py-2 text-sm sm:text-base rounded-lg border transition-all duration-200 font-semibold ${selectedCalcType === "historical" ? "border-teal-700 text-teal-700 bg-teal-50" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}>
                  <span className={`w-4 h-4 rounded-full shrink-0 border-2 flex items-center justify-center ${selectedCalcType === "historical" ? "border-teal-700" : "border-gray-400"}`}>{selectedCalcType === "historical" && <span className="w-2 h-2 bg-teal-700 rounded-full"/>}</span>
                  Historical Returns
                </button>

                {/* Future Value */}
                <button onClick={() => setSelectedCalcType("future")} className={`flex items-center gap-1.5 xs:gap-2 px-2 sm:px-4 py-2 text-sm sm:text-base rounded-lg border transition-all duration-200 font-semibold ${selectedCalcType === "future" ? "border-teal-700 text-teal-700 bg-teal-50" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}>
                  <span className={`w-4 h-4 rounded-full shrink-0 border-2 flex items-center justify-center ${selectedCalcType === "future" ? "border-teal-700" : "border-gray-400"}`}>{selectedCalcType === "future" && <span className="w-2 h-2 bg-teal-700 rounded-full"/>}</span>
                  Future Value
                </button>
              </div>
            </div>

            <motion.div layout transition={{ duration: 0.4, ease: "easeInOut" }}>
              <div className={`flex flex-col lg:flex-row gap-6 items-center sm:border-b sm:pb-5 ${selectedCalcType === "historical" ? "lg:h-151.75" : ""}`}>
                {/* Inputs */}
                <div className="w-full sm:p-3 lg:w-[41%] pb-0 shrink-0">
                  <AnimatePresence mode="wait">
                    {selectedCalcType === "historical" ? (
                      <motion.div key="historical" className="space-y-7 2lg:space-y-8" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3, ease: "easeInOut" }}>
                        <div className="mb-2 relative">
                          <div className="flex items-center gap-1 font-semibold text-left text-sm sm:text-base lg:text-lg text-[#222222] mb-3">Select Fund</div>

                          <div className="w-full relative group">
                            <button ref={buttonRef} onClick={() => setIsDropdownOpenFund((prev) => !prev)} className={`w-full py-2 px-2 sm:px-3 rounded-lg text-left bg-white border font-medium text-xs sm:text-sm lg:text-base text-primaryGreen transition-all duration-200 flex items-center justify-between xs:gap-px sm:gap-2 ${isDropdownOpenFund ? "border-[#a0a4ac]" : "border-[#cad3e2] hover:border-[#a0a4ac]"}`}>
                              <p className="line-clamp-1">{selectedFund?.scheme_name || "Select Funds To Calculate"}</p>
                              <Icon.dropdownDownArrow className={`shrink-0 transition duration-350 ${isDropdownOpenFund ? "rotate-180 text-primaryGreen" : "text-[#006b66a4] group-hover:text-primaryGreen"}`} />
                            </button>

                            <CustomDropdown isOpen={isDropdownOpenFund} onClose={() => setIsDropdownOpenFund(false)} className="top-full right-0 w-full mt-1 rounded-lg border border-[#e4e4e4] bg-white shadow-md z-10 px-1 sm:px-3 py-1.5 overflow-hidden" excludeRefs={[buttonRef]}>
                              <div className="relative">
                                <label className="block w-full text-base border border-[#888888] px-3 py-1 rounded-md mb-1">
                                  <input type="text" placeholder="Search fund..." className="w-full outline-hidden" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                                </label>

                                <div className="flex flex-col gap-1 w-full max-h-48 overflow-y-auto scrollbar-sm pr-1">
                                  {filteredFunds.length > 0 ? (
                                    filteredFunds.map((fund: AmcFund, ind: number) => (
                                      <button key={ind} onClick={() => handleSelectFund(fund)} className={`text-left px-1 py-2 rounded-md text-sm transition ${selectedFund?.scheme_name === fund.scheme_name ? "bg-gray-100 font-medium text-primaryGreen" : "hover:bg-gray-100"}`}>
                                        {fund.scheme_name}
                                      </button>
                                    ))
                                  ) : (
                                    <p className="text-primaryBlack text-center text-2sm p-2">No results Found</p>
                                  )}
                                </div>
                              </div>
                            </CustomDropdown>
                          </div>
                        </div>

                        <div className="w-full">
                          <div className="flex items-center gap-1 font-semibold text-left text-sm sm:text-base lg:text-lg text-[#222222] mb-3">Select Investment Type</div>

                          <div className="flex items-center gap-2 mb-0.5 xs:mb-2">
                            {["SIP", "Lumpsum"].map((opt) => (
                              <button key={opt} onClick={() => setInvestmentMode(opt)} className={`px-3 xs:px-4 py-1.5 xs:py-2 text-xs xs:text-sm xs:leading-5 md:text-base md:leading-6 leading-4 w-full sm:max-w-32 text-center rounded-lg border font-medium transition duration-350 ${investmentMode === opt ? "bg-primaryGreen text-white border-primaryGreen" : "border-gray-400 text-gray-700 hover:bg-[#e4faf8] hover:border-primaryGreen"}`}>
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>

                        <CalcInputValues label={investmentMode === "SIP" ? "SIP amount (Monthly)" : "Lumpsum amount"} min={minInvestmentAmount} max={maxInvestmentAmount} unit="₹" unitPosition="left" amountValue={investmentAmount} onChange={setInvestmentAmount} />

                        <div className="flex justify-between items-center gap-0 xxxs:gap-2 mb-2 relative">
                          <div className="flex items-center gap-1 font-semibold text-left text-sm sm:text-base lg:text-lg text-[#222222]">Tenure</div>

                          <div className="max-w-24 xxxs:max-w-26.5 xs:max-w-40 w-full relative group">
                            <button ref={buttonRef2} onClick={() => setIsDropdownOpenYear((prev) => !prev)} className={`w-full py-2 px-2 sm:px-3 rounded-lg text-left bg-white border font-medium text-xs sm:text-sm lg:text-base text-primaryGreen transition-all duration-200 flex items-center justify-between xs:gap-px sm:gap-2 ${isDropdownOpenYear ? "border-[#a0a4ac]" : "border-[#cad3e2] hover:border-[#a0a4ac]"}`}>
                              {selectedYear} {selectedYear === 1 ? "Year" : "Years"}
                              <Icon.dropdownDownArrow className={`transition duration-350 ${isDropdownOpenYear ? "rotate-180 text-primaryGreen" : "text-[#006b66a4] group-hover:text-primaryGreen"}`} />
                            </button>

                            <CustomDropdown isOpen={isDropdownOpenYear} onClose={() => setIsDropdownOpenYear(false)} className="top-full right-0 w-full mt-1 rounded-xl border border-[#e4e4e4] bg-white shadow-md z-10 p-1.5 overflow-hidden" excludeRefs={[buttonRef2]}>
                              <div className="flex flex-col gap-1">
                                {displayYears.map((freq) => (
                                  <button key={freq} onClick={() => handleSelectYear(freq)} className={`text-left px-1 py-2 rounded-md text-sm transition ${selectedYear === freq ? "bg-gray-100 font-medium text-primaryGreen" : "text-primaryBlack hover:bg-gray-100"}`}>
                                    {freq} {freq === 1 ? "Year" : "Years"}
                                  </button>
                                ))}
                              </div>
                            </CustomDropdown>
                          </div>
                        </div>

                        <div className="flex items-start gap-4 text-sm xs:text-base sm:text-xl font-medium bg-[#006b661a] text-primaryGreen rounded-xl px-4 py-3 text-left">
                          <LampSvg className="shrink-0 w-5 h-5 sm:w-7 sm:h-7" />
                          The Fund has delivered {fundsReturns}% Annualised Returns in {selectedYear} Year
                        </div>

                        <div className="hidden sm:flex items-center gap-4 2lg:gap-6 mb-0.5 xs:mb-2">
                          <a href={`${ROOT_URL}mutual-fund/${selectedSchemeData?.url}/returns/mso${selectedSchemeData?.id}`} className={`block px-3 xs:px-5 py-1.5 xs:py-2.5 text-xs xs:text-sm xs:leading-5 md:text-base md:leading-6 leading-4 w-fit text-center rounded-full font-medium transition duration-350 border border-gray-400 text-gray-700 hover:bg-[#f4fffe] hover:border-primaryGreen`}>
                            Scheme Return Analysis
                          </a>

                          <button onClick={() => dispatch(openInvestmentModal(schemeData))} className="shrink-0 px-8 2lg:px-14 py-1.5 xs:py-2.5 text-xs xs:text-sm xs:leading-5 md:text-base md:leading-6 leading-4 w-fit text-center rounded-full border font-medium transition duration-350 border-primaryGreen bg-primaryGreen text-white hover:bg-[#004744] hover:border-primaryGreen">
                            Start SIP
                          </button>
                        </div>
                      </motion.div>
                    ) : (
                      <motion.div key="future" className="space-y-8" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3, ease: "easeInOut" }}>
                        <div className="w-full">
                          <div className="flex items-center gap-1 font-semibold text-left text-sm sm:text-base lg:text-lg text-[#222222] mb-3">Select Investment Type</div>

                          <div className="flex items-center gap-2 mb-0.5 xs:mb-2">
                            {["SIP", "Lumpsum", "Target Amount"].map((opt) => (
                              <button key={opt} onClick={() => setInvestmentModeFuture(opt)} className={`px-3 xs:px-4 py-1.5 xs:py-2 text-xs xs:text-sm xs:leading-5 md:text-2sm md:leading-5.5 leading-4 w-full text-nowrap text-center rounded-lg border font-medium transition duration-350 ${investmentModeFuture === opt ? "bg-primaryGreen text-white border-primaryGreen" : "border-gray-400 text-gray-700 hover:bg-[#e4faf8] hover:border-primaryGreen"}`}>
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>

                        <CalcInputValues label={investmentModeFuture === "SIP" ? "SIP amount (Monthly)" : investmentModeFuture === "SIP" ? "Lumpsum amount" : "Target Amount"} min={2500} max={25000000} unit="₹" unitPosition="left" amountValue={investmentFutureAmount} onChange={setInvestmentFutureAmount} />

                        <CalcInputValues label="Tenure (Years)" unit="Year" min={1} max={80} unitPosition="right" amountValue={tenure} onChange={setTenure} useRangeSlider={false} />

                        <CalcInputValues label="Annual Returns" unit="%" min={1} max={100} unitPosition="right" amountValue={annualReturns} onChange={setAnnualReturns} useRangeSlider={false} allowDecimal />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {isTab && (
                    <button onClick={() => setIsOpen(true)} disabled={availableYears.length > 0 ? false : true} className={`lg:hidden mt-6 w-full max-w-fit mx-auto bg-primaryGreen rounded-xl text-sm font-medium text-white px-8 xs:px-14 py-2 xs:py-2.5 ${availableYears.length > 0 ? "" : "opacity-60"}`}>
                      Calculate
                    </button>
                  )}
                </div>

                {isLoadingReturns ? <LoadingChartSatate /> : availableYears.length > 0 ? selectedCalcType === "historical" && !isTab ? <RightPartHistoric className="hidden lg:block" calculationHistoric={calculationHistoric!} /> : null : <ChartShow />}

                {!isTab && selectedCalcType === "future" && <RightPartFuture className="hidden lg:block" calculationFuture={calculationFuture!} mode={investmentModeFuture} amount={investmentFutureAmount} year={tenure} />}
              </div>

              {!isMob && <MoreAmcCalc className="hidden sm:block" moreCalc={moreCalc} loading={loading} />}
            </motion.div>
          </div>
        </div>
      </section>

      {isMob && <MoreAmcCalc className="sm:hidden" moreCalc={moreCalc} loading={loading} />}

      {isTab && (
        <CustomModal isOpen={isOpen} onClose={() => setIsOpen(false)} maxSize="max-w-[80%]" useDragToClose isBlurOverlay className="bg-white rounded-b-none sm:rounded-4xl">
          {selectedCalcType === "historical" && <RightPartHistoric className="block lg:hidden" isOpen={isOpen} calculationHistoric={calculationHistoric!} />}

          {selectedCalcType === "future" && <RightPartFuture className="block lg:hidden" isOpen={isOpen} calculationFuture={calculationFuture!} mode={investmentModeFuture} amount={investmentFutureAmount} year={tenure} />}
        </CustomModal>
      )}
    </>
  );
}

function RightPartHistoric({ className, isOpen, calculationHistoric }: { isOpen?: boolean; className?: string; calculationHistoric: ChartReportData }) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  const { yearArray, totalInvested, totalAmount, profit } = useMemo(() => {
    const years = Object.keys(calculationHistoric || {});
    const invested: number[] = [];
    const amount: number[] = [];
    const prof: number[] = [];

    years.forEach((key) => {
      const values = calculationHistoric[key];
      invested.push(values?.total_invested ?? 0);
      amount.push(values?.total_amount ?? 0);
      prof.push(values?.profit ?? 0);
    });

    return { yearArray: years, totalInvested: invested, totalAmount: amount, profit: prof };
  }, [calculationHistoric]);

  const generatePlotLines = (count: number, step: number, thickness: number, color: string) => {
    const lines = [];
    for (let i = 1; i <= count; i++) {
      lines.push({
        color,
        width: thickness,
        value: i * step,
      });
    }
    return lines;
  };

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
    },
    title: {
      text: "",
    },
    accessibility: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: yearArray,
      lineColor: "#C2D8FD",
      labels: {
        style: {
          fontSize: "15px",
          fontWeight: "600",
          color: "#6d6d6d",
        },
      },
    },
    yAxis: {
      visible: true,
      title: {
        text: undefined,
      },
      labels: {
        enabled: false,
      },
      gridLineWidth: 0,
      plotLines: generatePlotLines(5, 2, 4, "#C2D8FD"),
      stackLabels: {
        enabled: true,
        backgroundColor: "transparent",
        style: {
          fontSize: "15px",
          color: "#6d6d6d",
          textOutline: "none",
        },
        formatter: function () {
          const currencyFormatter = new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            maximumFractionDigits: 0,
          });
          return currencyFormatter.format(this.total ?? 0);
        },
      },
    },
    legend: {
      align: "left",
      verticalAlign: "top",
      backgroundColor: "transparent",
      x: 0,
      y: 0,
      itemStyle: {
        fontSize: "15px",
        color: "#6d6d6d",
        fontFamily: "Roboto, sans-serif",
      },
    },
    tooltip: {
      headerFormat: "<b>{point.x}</b><br/>",
      pointFormat: "{series.name}: {point.y:,.2f}<br/>Total: {point.stackTotal:,.2f}",
      formatter: function () {
        // @ts-expect-error Highcharts type mismatch for 'this.point'
        const Total = formatIndianNumber(this?.point?.stackTotal ?? 0, { prefix: "₹" });

        return `<b>${this.x + 1}</b><br/>
          ${this.series.name}: ${formatIndianNumber(this.y ?? 0, { prefix: "₹" })}<br/>
          Total: ${Total}`;
      },
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderRadius: 10,
        groupPadding: 0.3,
        pointPadding: 0,
        borderWidth: 0,
        pointWidth: 40,
      },
    },
    series: [
      {
        type: "column",
        name: "Net Profit",
        data: profit,
        color: "#1FBD02",
      },
      {
        type: "column",
        name: "Invested Amount",
        data: totalInvested,
        color: "#1A51AA",
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 400,
          },
          chartOptions: {
            chart: {
              height: 300,
            },
            legend: {
              layout: "vertical",
              floating: true,
              x: -20,
              y: -15,
              itemStyle: {
                fontSize: "10px",
              },
            },
            plotOptions: {
              column: {
                pointWidth: 30,
              },
            },
          },
        },
      ],
    },
  };

  return (
    <>
      <div className={`w-full lg:w-[55%] lg:border-2 border-[#006b6633] rounded-2xl p-5 pt-0 sm:p-6 sm:pt-0 lg:p-6 bg-white sm:bg-[#f9fbfb] ${className}`}>
        <HighchartsReact ref={chartRef} highcharts={Highcharts} options={chartOptions} />

        <div className="flex justify-between font-semibold text-primaryBlack items-center bg-white xs:p-6 rounded-xl mx-auto mt-4">
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-[#666666] text-2sm">Invested Amt.</span>
            <span className="text-lg font-medium">{formatIndianNumber(totalInvested[totalInvested.length - 1], { prefix: "₹" })}</span>
          </div>

          <span className="text-xl text-gray-700">+</span>

          <div className="flex flex-col items-center gap-0.5">
            <span className="text-[#666666] text-2sm">Net Profit</span>
            <span className="text-lg font-medium">{formatIndianNumber(profit[profit.length - 1], { prefix: "₹" })}</span>
          </div>

          <span className="text-xl text-gray-700">=</span>

          <div className="flex flex-col items-center gap-0.5">
            <span className="text-primaryBlue text-2sm">Total Wealth</span>
            <span className={`text-lg font-medium ${totalAmount[totalAmount.length - 1] > 0 ? "text-primaryGreen" : "text-[#ff0000]"}`}>{formatIndianNumber(totalAmount[totalAmount.length - 1], { prefix: "₹" })}</span>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden sticky bottom-0 rounded-b-2xl gap-3 py-3 sm:py-5 bg-white border-t border-[#e4e4e4]">
          <a href={`${ROOT_URL}start-sip.php`} className="w-full block max-w-fit mx-auto bg-primaryGreen rounded-xl border border-primaryGreen text-sm font-medium text-white px-6 xs:px-14 py-2 xs:py-2.5">
            Start STP
          </a>
        </div>
      )}
    </>
  );
}

function RightPartFuture({ className, isOpen, calculationFuture, mode, amount, year }: { isOpen?: boolean; className?: string; calculationFuture: FutureCalculationResult; mode?: string; amount?: number; year?: number }) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  // Extract values safely
  const { invested, gain } = useMemo(() => {
    const invested = calculationFuture?.totalInvested ?? 0;
    const gain = calculationFuture?.profitAmount ?? 0;
    return { invested, gain };
  }, [calculationFuture]);

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "pie",
      height: 250,
      backgroundColor: "transparent",
    },
    title: {
      text: "",
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.percentage:.1f}%</b>",
    },
    accessibility: {
      point: { valueSuffix: "%" },
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        borderWidth: 2,
        cursor: "pointer",
        dataLabels: {
          enabled: false,
          format: "<b>{point.name}</b><br>{point.percentage:.1f}%",
          distance: 20,
        },
        animation: { duration: 1500 },
        center: ["50%", "50%"],
        size: "100%",
      },
    },
    series: [
      {
        type: "pie",
        name: "",
        innerSize: "50%", // donut-style
        data: [
          {
            name: "Invested Amount",
            y: invested,
            color: "#1FBD02",
          },
          {
            name: "Estimated Returns",
            y: gain,
            color: "#1A51AA",
          },
        ],
      },
    ],
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 400,
          },
          chartOptions: {
            chart: {
              height: 150,
            },
          },
        },
      ],
    },
  };

  return (
    <>
      <div className={`w-full lg:w-[55%] lg:border-2 border-[#006b6633] rounded-2xl p-5 pt-0 sm:p-6 bg-white sm:bg-[#f9fbfb] ${className}`}>
        {/* {mode != "Target Amount" ? (
          <p>Your Wealth Summary After {`${year} ${year === 1 ? "year" : "years"}`}</p>
        ) : (
          <p>
            {" "}
            To reach a target of {formatIndianNumber(Number(amount), { prefix: "₹" })} in {`${year} ${year === 1 ? "year" : "years"}`}
          </p>
        )} */}

        <p className="text-xl font-semibold">
          {mode != "Target Amount" ? (
            <>
              Your <span className="text-primaryBlue">Wealth Summary</span> After {`${year} ${year === 1 ? "year" : "years"}`}
            </>
          ) : (
            <>
              To reach a target of <span className="text-primaryBlue">{formatIndianNumber(Number(amount), { prefix: "₹" })}</span> in {`${year} ${year === 1 ? "year" : "years"}`}
            </>
          )}
        </p>

        <HighchartsReact ref={chartRef} highcharts={Highcharts} options={chartOptions} />

        <div className="mt-4 font-medium max-w-md mx-auto">
          {mode != "Target Amount" ? (
            <div className="flex items-center gap-0.5 text-2xl text-primaryBlack justify-between mb-5">
              <span>Future Value</span>
              <span>{formatIndianNumber(calculationFuture.futureValue, { prefix: "₹" })}</span>
            </div>
          ) : (
            <div className="text-2xl text-primaryBlue justify-between mb-5">
              You need to invest <span className="text-primaryGreen">{formatIndianNumber(Number(calculationFuture?.sipAmount ?? 0), { prefix: "₹" })}</span> Monthly
            </div>
          )}

          <div className="flex items-center justify-between gap-0.5 mb-3">
            <span className="text-primaryBlack text-lg">Invested Amoun</span>
            <span className="text-lg text-primaryGreen">{formatIndianNumber(calculationFuture.totalInvested, { prefix: "₹" })}</span>
          </div>

          <div className="flex items-center justify-between gap-0.5">
            <span className="text-primaryBlack text-lg">Estimated Returns</span>
            <span className="text-lg text-primaryBlue">{formatIndianNumber(calculationFuture.profitAmount, { prefix: "₹" })}</span>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="lg:hidden sticky bottom-0 rounded-b-2xl gap-3 py-3 sm:py-5 bg-white border-t border-[#e4e4e4]">
          <a href={`${ROOT_URL}start-sip.php`} className="w-full block max-w-fit mx-auto bg-primaryGreen rounded-xl border border-primaryGreen text-sm font-medium text-white px-6 xs:px-14 py-2 xs:py-2.5">
            Start STP
          </a>
        </div>
      )}
    </>
  );
}

function MoreAmcCalc({ className, loading, moreCalc }: { className: string; loading: boolean; moreCalc: MoreAmcCalculator[] }) { 

  return (
    <div className={`p-4 sm:py-6 mb-half-px sm:mb-0 ${className}`}>
      <div className="text-xl text-center font-semibold sm:font-mediam text-primaryBlack mb-5 sm:text-left">Explore Other Popular Calculators</div>

      <div className="flex items-center flex-nowrap gap-5 overflow-x-auto mb-4 pb-2  scrollbar-sm">
        {!loading
          ? moreCalc.map((calc, ind) => (
              <a key={ind} href={`${ROOT_URL}${calc.permalinks}`} className="group shrink-0 inline-flex items-center justify-center text-sm xs:text-base gap-3 py-2 px-3 xs:py-2.5 xs:px-5 border border-primaryGreen rounded-lg text-primaryGreen hover:text-[#004d49] transition hover:bg-[#e4faf8]">
                {calc.txttitle}
                <Icon.dropdownDownArrow className={`transition text-primaryGreen group-hover:text-[#004d49] -rotate-90`} />
              </a>
            ))
          : [1, 2, 3, 4].map((ind) => <div key={ind} className="h-12 shrink-0 bg-gray-200 w-60 rounded-lg animate-pulse" />)}
      </div>

      <a href={`${ROOT_URL}tools-and-calculators`} className="group w-fit shrink-0 flex items-center gap-2 justify-start text-lg font-medium text-primaryGreen mr-auto relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-primaryGreen after:transition-all after:duration-300 hover:after:w-full">
        View All Calculators
        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="transition-all duration-350 text-primaryGreen">
          <path d="M9.61984 4.1176L13.6665 8.16427L9.61984 12.2109" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M2.33029 8.16406H13.5503" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </a>
    </div>
  );
}

const LampSvg = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="29" height="27" viewBox="0 0 29 27" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M14.3416 3.32064C14.7529 3.32064 15.0894 2.99226 15.0894 2.59091V1.09496C15.0894 0.693613 14.7529 0.365234 14.3416 0.365234C13.9303 0.365234 13.5938 0.693613 13.5938 1.09496V2.59091C13.5938 2.99226 13.9303 3.32064 14.3416 3.32064Z" fill="#1F840D" />
    <path d="M7.23845 6.20311C7.42541 6.20311 7.61237 6.13014 7.76194 5.9842C8.06107 5.6923 8.06107 5.25447 7.76194 4.96257L6.67757 3.90447C6.37844 3.61257 5.92974 3.61257 5.6306 3.90447C5.33147 4.19636 5.33147 4.6342 5.6306 4.92609L6.71497 5.9842C6.86453 6.13014 7.05149 6.20311 7.23845 6.20311Z" fill="#1F840D" />
    <path d="M4.2809 11.6758H2.74784C2.33653 11.6758 2 12.0042 2 12.4055C2 12.8069 2.33653 13.1352 2.74784 13.1352H4.2809C4.69221 13.1352 5.02874 12.8069 5.02874 12.4055C5.02874 12.0042 4.69221 11.6758 4.2809 11.6758Z" fill="#1F840D" />
    <path d="M25.9293 11.6758H24.3963C23.985 11.6758 23.6484 12.0042 23.6484 12.4055C23.6484 12.8069 23.985 13.1352 24.3963 13.1352H25.9293C26.3407 13.1352 26.6772 12.8069 26.6772 12.4055C26.6772 12.0042 26.3407 11.6758 25.9293 11.6758Z" fill="#1F840D" />
    <path d="M22.004 3.90447L20.9197 4.96257C20.6205 5.25447 20.6205 5.6923 20.9197 5.9842C21.0692 6.13014 21.2562 6.20311 21.4432 6.20311C21.6301 6.20311 21.8171 6.13014 21.9666 5.9842L23.051 4.92609C23.3501 4.6342 23.3501 4.19636 23.051 3.90447C22.7519 3.61257 22.3032 3.61257 22.004 3.90447Z" fill="#1F840D" />
    <path d="M16.0962 24.3738L12.2822 25.1765C11.8709 25.2495 11.6092 25.6508 11.7214 26.0522C11.7961 26.3806 12.0953 26.636 12.4692 26.636C12.5066 26.636 12.5814 26.636 12.6188 26.5995L16.4327 25.7968C16.844 25.7238 17.1058 25.3225 16.9936 24.9211C16.8814 24.5562 16.4701 24.3008 16.0962 24.3738Z" fill="#395F6E" />
    <path
      d="M15.2367 4.74359C12.9932 4.48819 10.7123 5.21792 9.02967 6.67738C7.34704 8.13684 6.37485 10.2531 6.37485 12.4422C6.37485 14.5585 7.23486 16.5652 8.80532 18.0247C10.301 19.4476 11.161 21.272 11.161 23.1328C11.161 23.5341 11.4975 23.8625 11.9088 23.8625C11.9462 23.8625 12.021 23.8625 12.0584 23.826L16.8072 22.8044C16.8446 22.8044 16.8446 22.8044 16.882 22.7679C17.1811 22.7314 17.4428 22.476 17.4802 22.1476C17.742 20.5422 18.5272 19.0828 19.649 18.0612C21.4438 16.4193 22.3786 14.1206 22.1542 11.7125C21.9299 8.06386 18.9759 5.14494 15.2367 4.74359ZM18.4524 12.7706C18.415 12.7706 18.415 12.7706 18.3776 12.7706C18.0037 12.7706 17.6672 12.4787 17.6298 12.1139C17.4802 10.6179 16.2463 9.34089 14.7132 9.19494C14.3393 9.12197 14.0402 8.79359 14.0776 8.39224C14.115 7.99089 14.4889 7.699 14.9002 7.73548C17.1437 7.99089 18.9385 9.81522 19.1629 12.0044C19.2003 12.4058 18.8637 12.7341 18.4524 12.7706Z"
      fill="#1F840D"
    />
  </svg>
);

function ChartShow() {
  return (
    <div className="w-full lg:w-[55%] lg:border-2 border-[#6b000033] rounded-2xl p-5 pt-0 sm:p-6 sm:pt-0 lg:p-6 bg-red-200">
      <p className="text-xl text-[#ff0000] font-medium text-center">This fund has no returns for the selected year. Please choose a different fund for calculation</p>
    </div>
  );
}

function LoadingChartSatate() {
  return (
    <div className="hidden lg:h-141.5 lg:flex items-center justify-center lg:w-[55%]">
      <Image src="/next_assets/images/gif/chart_loading.gif" unoptimized alt="Loading Chart Animation" loading="eager" width={256} height={144} priority className="w-64 h-36" />
    </div>
  );
}
